import type { Price } from '@packages/domain/models/price';
import { isStablecoin } from '@packages/domain/utils/currency/currency';
import {
  ADAPTIS_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS,
  ADAPTIS_ELEMENTS_INSTRUCTIONS_PAYMENT_OPTIONS,
  AED_CARD_PAYMENT_OPTIONS,
  AED_CARD_WARNING_PORTALS,
  ALIPAY_PAYMENT_METHOD_PREFIX,
  BANK_TRANSFER_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS,
  FORM_REDIRECT_ELEMENTS_INSTRUCTIONS_ACTION,
  INDIAN_PARTNER_AHALIA,
  INDIAN_PARTNER_GSTIN_AHALIA,
  INDIAN_PARTNER_GSTIN_LULU,
  INDIAN_PARTNER_LICENCE_NO_AHALIA,
  INDIAN_PARTNER_LICENCE_NO_LULU,
  INDIAN_PARTNER_LULU,
  MPESA_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS,
  PAYID_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS,
  QR_CODE_ELEMENTS_INSTRUCTIONS_ACTION,
  WECHAT_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS,
} from 'constants/offers';
import get from 'lodash/get';
import type { Field } from 'models/field';
import type { Offer, OfferWithIds } from 'models/offer';
import type { RootState } from 'reducers/types';
import { getOrderOffer } from 'selectors/entities/order/order';
import {
  getPaymentOffer,
  getPaymentOfferFromPayment,
} from 'selectors/entities/payment/payment';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import { getSelectedOffer, getSelectedOfferId } from 'selectors/ui/ui';
import { getLocalCurrency } from '../currencies/currencies';
import { getFieldById } from '../fields/fields';

const NO_FIELDS: Field[] = [];

export const getOffer = (state: RootState, id: string) => {
  const offer = findOffer(state, id);

  if (!offer) {
    return {} as Offer;
  }

  return buildOfferWithEntities(state, offer);
};

export const getOfferName = (state: RootState, id: string) => {
  const offer = findOffer(state, id);

  return offer ? offer.name : '';
};

export const getLocalOffers = (state: RootState) => {
  const offers = getSingleOffers(state);
  const { code: localCurrency } = getLocalCurrency(state);

  return offers.filter(({ price }) => {
    return price.currency === localCurrency || isStablecoin(price.currency);
  });
};

export const allSingleOffersAreOnline = (state: RootState) => {
  const offers = getSingleOffers(state);

  return offers.every(offer => offer.paymentMethod !== 'bank_transfer');
};

export const allRecurringOffersAreOnline = (state: RootState) => {
  const offers = getRecurringOffers(state);

  return offers.every(offer => offer.paymentMethod !== 'bank_transfer');
};

export const allOffersAreOnline = (state: RootState) => {
  return allSingleOffersAreOnline(state) && allRecurringOffersAreOnline(state);
};

export const getForeignOffers = (state: RootState) => {
  const offers = getSingleOffers(state);
  const { code: localCurrency } = getLocalCurrency(state);

  return offers.filter(({ price }) => {
    return price.currency !== localCurrency && !isStablecoin(price.currency);
  });
};

const INDIAN_PARTNERS = ['lulu', 'ahal'] as const;
type IndianPartnerType = (typeof INDIAN_PARTNERS)[number];
const INDIAN_PARTNERS_DATA: Record<
  IndianPartnerType,
  {
    indianPartner: string;
    indianPartnerGstin: string;
    indianPartnerLicenceNo: string;
  }
> = {
  lulu: {
    indianPartner: INDIAN_PARTNER_LULU,
    indianPartnerGstin: INDIAN_PARTNER_GSTIN_LULU,
    indianPartnerLicenceNo: INDIAN_PARTNER_LICENCE_NO_LULU,
  },
  ahal: {
    indianPartner: INDIAN_PARTNER_AHALIA,
    indianPartnerGstin: INDIAN_PARTNER_GSTIN_AHALIA,
    indianPartnerLicenceNo: INDIAN_PARTNER_LICENCE_NO_AHALIA,
  },
};

const NO_INDIAN_PARTNER_DATA = {};

export const getIndianPartnerData = (paymentProcessor: string) => {
  if (!paymentProcessor) {
    return NO_INDIAN_PARTNER_DATA;
  }

  const paymentProcessorLower = paymentProcessor.toLowerCase();
  const partner = INDIAN_PARTNERS.find(partner =>
    paymentProcessorLower.includes(partner),
  );

  if (partner) {
    return INDIAN_PARTNERS_DATA[partner];
  }

  return NO_INDIAN_PARTNER_DATA;
};

export const indianPartnerData = (state: RootState) => {
  const { paymentMethodProcessor } = getOrderOffer(state);

  return getIndianPartnerData(paymentMethodProcessor);
};

export const isUsingDocCollectorLRS = (state: RootState) => {
  const { paymentOption } = getPaymentOfferFromPayment(state);

  if (!paymentOption) {
    return false;
  }

  return paymentOption.toUpperCase().includes('CASHFREE');
};

export const isLuluOfferSelected = (state: RootState) => {
  const { paymentOption } = getOrderOffer(state);

  if (!paymentOption) {
    return false;
  }

  return paymentOption.toUpperCase().includes('LULU');
};

export const isCashfreeOfferSelected = (state: RootState) => {
  const { paymentOption } = getOrderOffer(state);

  if (!paymentOption) {
    return false;
  }

  return paymentOption.toUpperCase().includes('CASHFREE');
};

export const isHdfcOnlineOfferSelected = (state: RootState) => {
  const { paymentOption } = getOrderOffer(state);
  if (!paymentOption) {
    return false;
  }
  const normalized = paymentOption.toUpperCase();
  return normalized.includes('HDFC') && normalized.includes('ONLINE');
};

export const isDirectDebitOfferSelected = (state: RootState) => {
  const offer = getSelectedOffer(state);

  return offer?.paymentMethod === 'direct_debit';
};

export const isOppOfferSelected = (state: RootState) => {
  return /opp/i.test(getPaymentOffer(state)?.paymentOption ?? '');
};

export const getRepeatPaymentOffer = (state: RootState) => {
  return get(state, 'entities.repeatPaymentOffer');
};

export const shouldShowEURateDisclaimer = (state: RootState) => {
  const {
    entities: {
      offers: { byId: offers, ids },
    },
  } = state;

  const hasMarkupRate = (id: string) => !!offers[id].markupRate;
  return ids.some(hasMarkupRate);
};

const makeElementsInstructionsSelector =
  (methods: string[]) => (state: RootState) => {
    const { paymentMethodInternalName } = getPaymentOfferFromPayment(state);
    return methods.includes(paymentMethodInternalName);
  };

export const shouldUseBankTransferElementsInstructions =
  makeElementsInstructionsSelector(
    BANK_TRANSFER_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS,
  );
export const shouldUseMpesaElementsInstructions =
  makeElementsInstructionsSelector(MPESA_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS);
export const shouldUseWeChatElementsInstructions =
  makeElementsInstructionsSelector(
    WECHAT_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS,
  );
export const shouldUsePayIdElementsInstructions = (state: RootState) => {
  const { paymentMethodInternalName } = getPaymentOfferFromPayment(state);
  const { elementsInstructions } = state.entities.payment;

  return (
    PAYID_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS.includes(
      paymentMethodInternalName,
    ) &&
    elementsInstructions?.action?.type === QR_CODE_ELEMENTS_INSTRUCTIONS_ACTION
  );
};

export const shouldUseAlipayElementsInstructions = (state: RootState) => {
  const { paymentMethodInternalName } = getPaymentOfferFromPayment(state);
  const { elementsInstructions } = state.entities.payment;

  return (
    !!paymentMethodInternalName?.startsWith(ALIPAY_PAYMENT_METHOD_PREFIX) &&
    elementsInstructions?.action?.type ===
      FORM_REDIRECT_ELEMENTS_INSTRUCTIONS_ACTION
  );
};

export const shouldUseAdaptisElementsInstructions = (state: RootState) => {
  const { paymentMethodInternalName, paymentOption } =
    getPaymentOfferFromPayment(state);
  const { elementsInstructions } = state.entities.payment;

  return (
    (ADAPTIS_ELEMENTS_INSTRUCTIONS_PAYMENT_METHODS.includes(
      paymentMethodInternalName,
    ) ||
      ADAPTIS_ELEMENTS_INSTRUCTIONS_PAYMENT_OPTIONS.includes(paymentOption)) &&
    elementsInstructions?.action?.type ===
      FORM_REDIRECT_ELEMENTS_INSTRUCTIONS_ACTION
  );
};

export const getSingleOffers = (state: RootState) => {
  const {
    entities: { offers: { ids } = {} },
  } = state;

  return ids?.map(id => getSingleOffer(state, id)) || [];
};

export const getRecurringOffers = (state: RootState) => {
  const {
    entities: { recurringOffers: { ids } = {} },
  } = state;

  return ids?.map(id => getRecurringOffer(state, id)) || [];
};

export const getRecurringOffer = (state: RootState, id: string) => {
  const {
    entities: {
      recurringOffers: { byId },
    },
  } = state;

  return buildOfferWithEntities(state, byId[id]);
};

export const findOffer = (state: RootState, id: string) => {
  const {
    entities: {
      offers: { byId: singleOffersById } = {},
      recurringOffers: { byId: recurringOffersById } = {},
    },
  } = state;

  return recurringOffersById?.[id]
    ? recurringOffersById[id]
    : singleOffersById?.[id];
};

export const getOffersRecurringConfigurationId = (state: RootState) => {
  return state.entities.recurringOffers.metadata.configurationId;
};

export const findSingleOffer = (state: RootState, id: string) => {
  const {
    entities: { offers: { byId: singleOffersById } = {} },
  } = state;

  return singleOffersById?.[id];
};

export const selectedSingleOffer = (state: RootState) => {
  const selectedOfferId = getSelectedOfferId(state);
  const {
    entities: { offers: { byId: singleOffersById } = {} },
  } = state;

  return singleOffersById?.[selectedOfferId];
};

export const findRecurringOffer = (state: RootState, id: string) => {
  const {
    entities: { recurringOffers: { byId: recurringOffersById } = {} },
  } = state;

  return recurringOffersById?.[id];
};

export const selectedRecurringOffer = (state: RootState) => {
  const selectedOfferId = getSelectedOfferId(state);
  const {
    entities: { recurringOffers: { byId: recurringOffersById } = {} },
  } = state;

  return recurringOffersById?.[selectedOfferId];
};

const getFee = (offer: Offer, price: Price<string>) => {
  const { currency } = price;
  const { fees: value } = offer;

  return { currency, value };
};

const getSingleOffer = (state: RootState, id: string) => {
  const {
    entities: {
      offers: { byId },
    },
  } = state;

  return buildOfferWithEntities(state, byId[id]);
};

const buildOfferWithEntities = (state: RootState, offer: OfferWithIds) => {
  const {
    entities: { prices },
  } = state;

  const price = prices[offer.price];
  const fee = getFee(offer, price);
  const fields = (offer.fields || NO_FIELDS).map(field =>
    getFieldById(state, field),
  );
  const recipientId = getRecipientId(state);
  const requiresAedWarning =
    AED_CARD_WARNING_PORTALS.includes(recipientId) &&
    AED_CARD_PAYMENT_OPTIONS.includes(offer.paymentOption);

  return { ...offer, price, fee, fields, requiresAedWarning };
};

export const getRecurringOffersMetadata = (state: RootState) => {
  return state.entities.recurringOffers.metadata;
};
